import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Star, Check, Lock, Palette, Frame, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { KidAvatar } from "../components/Brand";
import { useAuth } from "../context/AuthContext";
import { kidApi, errMsg } from "../lib/api";
import { SHOP_COLORS, SHOP_FRAMES } from "../lib/shop";

const ShopItem = ({ item, preview, shop, onBuy, onEquip }) => {
  const owned = shop.owned.includes(item.id);
  const equipped = item.value ? shop.color === item.value : shop.frame === item.id;
  const short = shop.stars < item.price;
  return (
    <div data-testid={`shop-item-${item.id}`} className="zn-card zn-lift bg-white p-3 sm:p-4 flex flex-col items-center gap-2 text-center">
      {preview}
      <p className="font-display font-bold leading-tight">{item.name}</p>
      {item.special && <span className="text-[10px] font-bold uppercase tracking-wider text-[#7B5CFA]">Special</span>}
      {equipped ? (
        <span data-testid={`shop-equipped-${item.id}`} className="zn-chip !min-h-0 !py-1 bg-[#B8F2C9] text-xs"><Check size={14} /> Wearing</span>
      ) : owned ? (
        <button data-testid={`shop-equip-${item.id}`} onClick={() => onEquip(item.id)} className="zn-chip !min-h-0 !py-1 bg-[#9BD0FF] text-xs">Wear it</button>
      ) : (
        <button data-testid={`shop-buy-${item.id}`} disabled={short} onClick={() => onBuy(item)} className={`zn-chip !min-h-0 !py-1 text-xs ${short ? "bg-[#EDE7DD] opacity-70" : "bg-[#FFD23F]"}`}>
          {short ? <Lock size={13} /> : <Star size={13} fill="#1B1A2E" />} {item.price}
        </button>
      )}
    </div>
  );
};

export default function StarShop() {
  const { kid, setKid } = useAuth();
  const [shop, setShop] = useState(null);
  useEffect(() => { kidApi.get("/kid/shop").then((r) => setShop(r.data)).catch((e) => toast.error(errMsg(e))); }, []);

  const apply = (data) => { setShop(data); setKid((k) => ({ ...k, color: data.color, frame: data.frame, stars: data.stars })); };
  const buy = async (item) => {
    try { apply((await kidApi.post("/kid/shop/buy", { item: item.id })).data); toast.success(`You got ${item.name}! Looking great.`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const equip = async (id) => {
    try { apply((await kidApi.post("/kid/shop/equip", { item: id })).data); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!shop) return <p className="font-display text-xl">Opening the shop…</p>;
  const props = { shop, onBuy: buy, onEquip: equip };

  return (
    <div data-testid="star-shop">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Link to="/kid/games" data-testid="shop-back-btn" className="zn-chip bg-white !px-3"><ArrowLeft size={16} /></Link>
        <h1 className="text-3xl sm:text-5xl font-bold flex-1">Star Shop</h1>
        <span data-testid="shop-stars" className="zn-chip bg-[#FFD23F]"><Star size={16} fill="#1B1A2E" /> {shop.stars} stars</span>
      </div>
      <div className="zn-card zn-pop bg-[#7B5CFA] text-white p-5 sm:p-8 flex items-center gap-5 sm:gap-8 mb-10">
        <KidAvatar avatar={kid.avatar} color={shop.color} frame={shop.frame} size={112} className="shadow-[5px_5px_0_0_#1B1A2E]" />
        <div className="flex-1 min-w-0">
          <p className="font-display text-xl sm:text-3xl font-bold">Looking awesome, {kid.name}!</p>
          <p className="opacity-90 text-sm sm:text-base">Spend stars from games on colors and frames. Everyone sees them on your videos.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {shop.color !== shop.base_color && <button data-testid="shop-reset-color" onClick={() => equip("color_original")} className="zn-chip !min-h-0 !py-1 bg-white text-[#1B1A2E] text-xs"><Undo2 size={13} /> My first color</button>}
            {shop.frame && <button data-testid="shop-remove-frame" onClick={() => equip("frame_none")} className="zn-chip !min-h-0 !py-1 bg-white text-[#1B1A2E] text-xs"><Undo2 size={13} /> No frame</button>}
          </div>
        </div>
      </div>
      <h2 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2 mb-4"><Palette size={22} /> Avatar colors</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-5 mb-10" data-testid="shop-colors">
        {SHOP_COLORS.map((c) => <ShopItem key={c.id} item={c} {...props} preview={<KidAvatar avatar={kid.avatar} color={c.value} size={64} />} />)}
      </div>
      <h2 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2 mb-4"><Frame size={22} /> Profile frames</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-5" data-testid="shop-frames">
        {SHOP_FRAMES.map((f) => <ShopItem key={f.id} item={f} {...props} preview={<KidAvatar avatar={kid.avatar} color={shop.color} frame={f.id} size={64} />} />)}
      </div>
    </div>
  );
}
